import type { DerivedStats, EmitFn, EnemyState, GameState, MasterDie, Phase, WorldRef } from './types';
import { BALANCE } from '../data/balance';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';
import { createStarterDie } from './dice';
import { waveOf } from './economy';
import { makeBossOf, rollEnemy } from './enemies';
import { gateBlocks, gateStatus } from './gate';
import type { Rng } from './rng';
import { grantArtisanLevel, nodeLevel } from './skilltree';
import { ZONES } from '../data/enemies';
import { ENDLESS_FROM, currentBossId, drawBoss, drawNextWorld, isEndless, worldOf } from './world';

/**
 * Machine à états de la run — MVP_DESIGN §1.
 * Les fonctions mutent `ctx.state` et signalent les changements via `ctx`.
 */
export interface PhaseCtx {
  state: GameState;
  rng: Rng;
  stats: DerivedStats;
  /** Réglages d'équilibrage résolus (surcharges de debug incluses). */
  balance?: ResolvedBalance;
  emit: EmitFn;
  setPhase(phase: Phase): void;
  /** Oublie les lancers en vol et ferme la fenêtre de grâce. */
  resetFlight(): void;
  /** Ouvre la fenêtre de grâce si des dés sont encore en vol. */
  openGrace(): void;
  /** File d'ennemis de l'Invasion (mutée sur place). */
  queue: EnemyState[];
  /**
   * Prépare l'ennemi qui **apparaît** (v0.47.0, *Du Vide 3* — A95-2), avant
   * l'événement `spawn`. Absent : rien à faire.
   */
  prepareEnemy?(enemy: EnemyState): void;
}

/**
 * Remet à zéro le combat **et son journal de lancers** (COMMENTS v0.3.0 §2) :
 * c'est le seul endroit où `state.combat.throws` est vidé, pour que les écrans
 * de fin (`expeditionEnd`, `invasionWon`, `runLost`) puissent
 * encore lire le combat qui vient de se terminer. v0.57.0 (A122-11) : rien
 * d'autre ne remplace `state.combat` — en Accalmie, c'est encore le dernier
 * combat, que `Game.lastCombat()` rend à l'interface.
 */
function resetCombat(ctx: PhaseCtx, duration: number, kind: 'expedition' | 'invasion'): void {
  const s = ctx.state;
  s.timeTotal = duration;
  s.timeRemaining = duration;
  s.timeSpent = 0;
  s.timeMax = duration * BALANCE.maxCombatFactor;
  s.cooldownRemaining = 0;
  s.spawnDelay = 0;
  s.pendingGold = 0;
  s.combat = {
    kills: 0,
    gold: 0,
    bestThrow: 0,
    throws: [],
    kind,
    durationPlayed: 0,
    // L'Enfer de *666* et la séquence de *Premiers* ne survivent pas au combat.
    inferno: false,
    primesQueue: [],
    // Effets d'ennemis (v0.8.0) : l'accélération du chrono par les Poissons
    // Lune, la bonification de PV laissée par un Ingénieur, la perte de
    // l'Expédition et l'abandon sont tous **propres au combat**.
    timeSpeed: 1,
    nextEnemyHpMult: 1,
    lost: false,
    abandoned: false,
    // Séries de *Criticité* et de *Flux arcanique* (A77) : elles vivent au
    // niveau du combat (Expédition ou Invasion), comme *Préparation* — dans
    // `state.combat` depuis A88, jamais sauvegardées.
    critStreak: 0,
    spellStreak: 0,
    // Indicateurs du HUD (A88) : secondes de *Temporel* / *42*, ennemis apparus.
    secondsGained: 0,
    enemiesSpawned: 0,
    // Matériaux (v0.47.0, A94-16, A95-3) : tout ce qui est « précédent » ou
    // « reporté » est propre au combat — l'excédent du Silicium est perdu.
    woodUsed: false,
    overkillCarry: 0,
    lastTotal: 0,
    lastBasicSum: 0,
    lastMasterFaces: [],
    // A122-6 : l'Électrification de *Silicium 5* repart de zéro à chaque combat.
    electrifyStacks: 0,
    // A122-15 : dégâts et morts du poison, pour le rapport de combat.
    poisonDamage: 0,
    poisonKills: [],
    // v0.49.0 (A98) : copies de *42*, *Tempête* et maximum du lancer précédent
    // (*Rouleau Compresseur*) sont propres au combat.
    fortyTwoCopies: [],
    stormRemaining: 0,
    lastMaxFace: 0,
  };
  // L'assommement ne survit pas non plus d'un combat à l'autre (A29).
  s.stunRemaining = 0;
  s.invasionReady = false;
  ctx.resetFlight();
}

/**
 * Compteurs **de run** de la v0.26.0 (A72) remis à zéro en fin de run :
 * tractopelle (et son armement), compteurs de *Guinsoo* — et, par sûreté, les
 * séries de *Criticité* et de *Flux arcanique* du dernier combat (elles
 * repartent de toute façon de zéro à chaque combat, `resetCombat`).
 */
export function resetRunCounters(state: GameState): void {
  state.combat.critStreak = 0;
  state.combat.spellStreak = 0;
  state.backhoe = 0;
  state.backhoeArmed = false;
  state.guinsoo = {};
}

/**
 * **Mode infini** (phase 5, A78, A82) : tire le monde et le boss du niveau
 * courant s'il est infini (≥ 11) et qu'il en manque un. Ne retire **jamais**
 * un monde ou un boss déjà posé (seul ce qui manque est rempli : monde par
 * `drawNextWorld` sans précédent, puis boss par `drawBoss`). En campagne, ne
 * fait rien. Sert au chargement d'une partie infinie sans monde (ou au monde
 * illisible), à la victoire différée et au debug ; le monde de chaque combat
 * suivant est tiré par `drawNextCombatWorld`.
 */
export function ensureWorld(ctx: Pick<PhaseCtx, 'state' | 'rng'>): void {
  const s = ctx.state;
  if (!isEndless(s.level)) return;
  if (s.world === null) s.world = drawNextWorld(null, ctx.rng);
  if (s.bossId === null) s.bossId = drawBoss(ctx.rng);
}

/**
 * **Monde par combat** (v0.40.0, retour GD du 11/10/2026) : en mode infini,
 * tire le monde du **prochain** combat, jamais le même que `previous` (celui
 * du combat qui vient de finir) et jamais deux procéduraux de suite
 * (`drawNextWorld`). Le boss du niveau n'est pas touché. En campagne, ne fait
 * rien.
 */
export function drawNextCombatWorld(ctx: Pick<PhaseCtx, 'state' | 'rng'>, previous: WorldRef | null): void {
  const s = ctx.state;
  if (!isEndless(s.level)) return;
  s.world = drawNextWorld(previous, ctx.rng);
}

/** Retour en campagne : plus de monde ni de boss tirés. */
function clearWorld(s: GameState): void {
  s.world = null;
  s.bossId = null;
}

/** Pose `state.gateReached` (A112) et émet `gate` s'il change. */
function setGateReached(ctx: Pick<PhaseCtx, 'state' | 'emit'>, reached: boolean): void {
  const s = ctx.state;
  if (s.gateReached === reached) return;
  s.gateReached = reached;
  ctx.emit('gate', { reached });
}

/**
 * **Passage au niveau suivant**, commun à `acknowledgeInvasionWon` et à
 * `claimGate` (A112) : niveau + 1, jour 1, meilleur niveau, monde et boss.
 * Ne change pas de phase.
 */
function advanceLevel(ctx: PhaseCtx): void {
  const s = ctx.state;
  const previous = worldOf(s);
  s.level += 1;
  s.day = 1;
  s.invasionReady = false;
  s.enemy = null;
  s.stats.bestLevel = Math.max(s.stats.bestLevel, s.level);
  // Nouveau niveau : en campagne, pas de monde tiré ; en mode infini, le monde
  // du prochain combat (différent de celui de l'Invasion qui vient d'être
  // gagnée — la zone 10 au passage 10 → 11) puis le boss du **nouveau**
  // niveau, tiré une fois pour tout le niveau.
  clearWorld(s);
  drawNextCombatWorld(ctx, previous);
  ensureWorld(ctx);
}

/** Nouvelle run depuis l'écran titre : niveau 1, jour 1, Expédition immédiate. */
export function startRun(ctx: PhaseCtx): void {
  const s = ctx.state;
  s.level = 1;
  s.day = 1;
  clearWorld(s);
  s.lastRun = null;
  s.runGold = 0;
  resetRunCounters(s);
  // A112 : une run neuve repart du niveau 1, rien n'est dû.
  setGateReached(ctx, false);
  startExpedition(ctx);
}

export function startExpedition(ctx: PhaseCtx): void {
  const s = ctx.state;
  // Le combat joue `state.world`, tiré à la fin du précédent (ou au
  // chargement) : aucun tirage en entrée. Sécurité : un niveau infini sans
  // monde en reçoit un (jamais remplacé).
  if (isEndless(s.level) && s.world === null) ensureWorld(ctx);
  s.wave = waveOf(s.level, s.day);
  resetCombat(ctx, ctx.stats.expeditionDuration, 'expedition');
  s.enemyIndex = 0;
  s.enemiesTotal = 0;
  ctx.queue.length = 0;
  const enemy = rollEnemy(s.wave, ctx.rng, ctx.balance ?? DEFAULT_BALANCE, worldOf(s));
  ctx.prepareEnemy?.(enemy);
  s.enemy = enemy;
  s.combat.enemiesSpawned = 1;
  ctx.setPhase('expedition');
  ctx.emit('spawn', { enemy });
  ctx.emit('timer', { remaining: s.timeRemaining, total: s.timeTotal });
}

/**
 * Fin d'Expédition **réussie** : l'or accumulé est crédité. Appelée
 * automatiquement par `tick()` au bout du chrono, et par `Game.handleKill` dès
 * le `BALANCE.expeditionEnemyCap`-ième ennemi tué (A88 : le 50e ennemi termine
 * l'Expédition, réussie ; le temps restant n'est converti en rien).
 */
export function endExpedition(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase !== 'expedition') return;
  const earned = s.pendingGold;
  s.pendingGold = 0;
  s.timeRemaining = 0;
  s.combat.durationPlayed = s.timeSpent;
  if (earned > 0) {
    s.gold += earned;
    s.stats.totalGold += earned;
  }
  s.runGold += earned;
  s.stats.daysPlayed += 1;
  ctx.setPhase('expeditionEnd');
  if (earned > 0) ctx.emit('gold', { gold: s.gold });
  ctx.openGrace();
}

/**
 * **Expédition perdue** — arbitrage **A28** (Serpent ; depuis la v0.52.0, A102,
 * plus aucun monstre ne l'appelle, le Serpent divisant le temps restant par 2) :
 * le combat s'arrête net,
 * l'or en attente est **perdu**, le jour est consommé comme une fin normale et
 * la run continue au jour suivant. C'est la seule différence avec
 * `endExpedition` : aucun or n'est crédité et `combat.lost` le signale à
 * l'écran de fin.
 *
 * En **Invasion**, la règle vaut défaite : l'appelant passe par
 * `endInvasion(ctx, false)`, pas par cette fonction.
 */
export function loseExpedition(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase !== 'expedition') return;
  s.pendingGold = 0;
  s.timeRemaining = 0;
  s.combat.lost = true;
  s.combat.durationPlayed = s.timeSpent;
  s.stats.daysPlayed += 1;
  ctx.setPhase('expeditionEnd');
  ctx.openGrace();
}

/**
 * **Abandon de la run** — arbitrage **A30** : le bouton « Abandonner » du HUD
 * de combat vaut défaite, depuis une Expédition comme depuis une Invasion.
 * L'or en attente est perdu, la run est comptée, l'écran de fin de run s'ouvre
 * (« Run abandonnée ») et la suivante repartira au niveau 1.
 *
 * Aucune fenêtre de grâce : le joueur a décidé d'arrêter, les dés encore en vol
 * ne doivent pas pouvoir sauver la run (contrairement à une fin de chrono).
 * Renvoie `false` hors combat.
 */
export function abandonRun(ctx: PhaseCtx): boolean {
  const s = ctx.state;
  if (s.phase !== 'expedition' && s.phase !== 'invasion') return false;
  s.pendingGold = 0;
  s.timeRemaining = 0;
  s.combat.abandoned = true;
  s.combat.durationPlayed = s.timeSpent;
  s.stats.runs += 1;
  s.lastRun = {
    level: s.level,
    day: s.day,
    runs: s.stats.runs,
    bestLevel: s.stats.bestLevel,
    totalGold: s.stats.totalGold,
    goldEarned: s.runGold,
    abandoned: true,
  };
  ctx.resetFlight();
  ctx.setPhase('runLost');
  return true;
}

export function startInvasion(ctx: PhaseCtx): void {
  const s = ctx.state;
  s.day = BALANCE.invasionDay;
  s.wave = waveOf(s.level, BALANCE.invasionDay);
  resetCombat(ctx, ctx.stats.invasionDuration, 'invasion');
  s.enemyIndex = 0;
  s.enemiesTotal = BALANCE.invasionEnemyCount;

  // 4 ennemis tirés dans le pool de la vague (du monde, en mode infini), puis
  // le boss (celui de la zone, ou celui tiré pour le niveau infini).
  if (isEndless(s.level)) ensureWorld(ctx);
  const bal = ctx.balance ?? DEFAULT_BALANCE;
  const world = worldOf(s);
  ctx.queue.length = 0;
  for (let i = 2; i < BALANCE.invasionEnemyCount; i++) {
    ctx.queue.push(rollEnemy(s.wave, ctx.rng, bal, world));
  }
  ctx.queue.push(makeBossOf(currentBossId(s), s.wave, bal));

  const first = rollEnemy(s.wave, ctx.rng, bal, world);
  ctx.prepareEnemy?.(first);
  s.enemy = first;
  s.combat.enemiesSpawned = 1;
  ctx.setPhase('invasion');
  ctx.emit('spawn', { enemy: first });
  ctx.emit('timer', { remaining: s.timeRemaining, total: s.timeTotal });
}

/**
 * Fin d'Invasion. `won` : le boss est mort avant la fin du chrono. L'Invasion
 * gagnée du niveau 10 (le Tricorne) ouvre l'écran de **victoire** (A82) au
 * lieu d'`invasionWon` ; l'or est crédité de la même façon.
 *
 * Mur payant (A112) : sans achat, l'Invasion gagnée au niveau 2 (ou au-delà,
 * sauvegarde ancienne) mène bien à `invasionWon`, or crédité, mais le
 * meilleur niveau ne monte pas : c'est l'acquittement qui ouvre le mur.
 */
export function endInvasion(ctx: PhaseCtx, won: boolean): void {
  const s = ctx.state;
  // La victoire est encore acceptée pendant la fenêtre de grâce d'une défaite.
  if (s.phase !== 'invasion' && !(won && s.phase === 'runLost')) return;
  const earned = s.pendingGold;
  s.pendingGold = 0;
  s.combat.durationPlayed = s.timeSpent;
  if (won) {
    if (earned > 0) {
      s.gold += earned;
      s.stats.totalGold += earned;
      s.runGold += earned;
    }
    // A112 : un joueur arrêté par le mur payant n'atteint pas le niveau
    // suivant — le meilleur niveau reste honnête (classements, Abîme).
    if (!gateBlocks(s.level, s.entitled)) s.stats.bestLevel = Math.max(s.stats.bestLevel, s.level + 1);
    ctx.setPhase(s.level === ZONES.length ? 'victory' : 'invasionWon');
    if (earned > 0) ctx.emit('gold', { gold: s.gold });
  } else {
    // Défaite : l'or de l'Invasion est perdu (MVP_DESIGN §5).
    s.stats.runs += 1;
    s.lastRun = {
      level: s.level,
      day: s.day,
      runs: s.stats.runs,
      bestLevel: s.stats.bestLevel,
      totalGold: s.stats.totalGold,
      // L'or de la run qui vient de se terminer (COMMENTS §1 réponse 15 / Q15).
      goldEarned: s.runGold,
    };
    ctx.setPhase('runLost');
  }
  ctx.openGrace();
}

/** Retour à l'Accalmie depuis n'importe quel écran de fin. */
export function goToAccalmie(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase === 'invasionWon') return acknowledgeInvasionWon(ctx);
  if (s.phase === 'victory') return acknowledgeVictory(ctx);
  if (s.phase === 'runLost') return acknowledgeRunLost(ctx);
  if (s.phase === 'gate') {
    dismissGate(ctx);
    return;
  }
  if (s.phase === 'expeditionEnd') {
    // `day` désigne toujours le jour à jouer ; après le jour 5 c'est l'Invasion.
    if (s.day < BALANCE.daysPerLevel) s.day += 1;
    else s.invasionReady = true;
    // Mode infini : Expédition terminée ou perdue (Serpent), le monde du
    // prochain combat est tiré ici, différent de celui qui vient d'être joué ;
    // l'en-tête de l'Accalmie l'annonce.
    drawNextCombatWorld(ctx, worldOf(s));
  }
  s.enemy = null;
  ctx.resetFlight();
  ctx.setPhase('accalmie');
}

/** Lance l'Expédition du jour courant depuis l'Accalmie. */
export function startNextDay(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase !== 'accalmie' || s.invasionReady) return;
  startExpedition(ctx);
}

/**
 * Invasion gagnée acquittée : passage au niveau suivant, en Accalmie.
 *
 * **Mur payant** (v0.55.0, A112) : sans achat, au dernier niveau gratuit (ou
 * au-delà, sauvegarde ancienne), pas de passage — le niveau suivant devient
 * **dû** (`gateReached`), le joueur reste au même niveau, jour 1, et l'écran
 * d'achat s'ouvre (`gate`). Ce n'est pas une nouvelle run : or, dés et
 * compteurs de run restent.
 */
export function acknowledgeInvasionWon(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (gateBlocks(s.level, s.entitled)) {
    setGateReached(ctx, true);
    s.day = 1;
    s.invasionReady = false;
    s.enemy = null;
    // Mode infini (sauvegarde ancienne non achetée) : le niveau est rejoué,
    // le monde du prochain combat est retiré comme après tout combat. Sans
    // effet en campagne.
    drawNextCombatWorld(ctx, worldOf(s));
    ctx.resetFlight();
    ctx.setPhase('gate');
    return;
  }
  advanceLevel(ctx);
  setGateReached(ctx, false);
  ctx.resetFlight();
  ctx.setPhase('accalmie');
}

/**
 * Ouvre l'écran d'achat depuis l'Accalmie (A112) — seulement pour un joueur
 * **bloqué** par le mur (`gateStatus(state).locked`) : un joueur qui a acheté
 * n'a pas d'écran d'achat. Renvoie `true` si la phase `gate` est ouverte.
 */
export function openGate(ctx: PhaseCtx): boolean {
  const s = ctx.state;
  if (s.phase !== 'accalmie' || !gateStatus(s).locked) return false;
  ctx.setPhase('gate');
  return true;
}

/**
 * Ferme l'écran d'achat (A112) : retour à l'Accalmie au **même niveau** ; le
 * joueur rejoue librement les niveaux gratuits. Le jour n'est pas touché (jour 1
 * après le mur de l'Invasion ; celui de l'Accalmie quittée si l'écran a été
 * ouvert par `openGate`). Renvoie `false` hors de la phase `gate`.
 */
export function dismissGate(ctx: PhaseCtx): boolean {
  const s = ctx.state;
  if (s.phase !== 'gate') return false;
  s.enemy = null;
  ctx.resetFlight();
  ctx.setPhase('accalmie');
  return true;
}

/**
 * **Niveau dû** réclamé après achat (A112) : si le jeu est acheté et que le
 * boss du niveau courant a été battu sans achat (`gateReached`), applique
 * exactement le passage de niveau d'`acknowledgeInvasionWon`, sans rebattre le
 * boss, depuis l'écran d'achat ou l'Accalmie. Un joueur qui a rejoué entre-temps
 * (jour 3 du niveau 2) passe au niveau 3 jour 1 : le niveau était dû. Renvoie
 * `false` (et ne change rien) dans tout autre cas.
 */
export function claimGate(ctx: PhaseCtx): boolean {
  const s = ctx.state;
  if (!s.entitled || !s.gateReached) return false;
  if (s.phase !== 'gate' && s.phase !== 'accalmie') return false;
  advanceLevel(ctx);
  setGateReached(ctx, false);
  ctx.resetFlight();
  ctx.setPhase('accalmie');
  return true;
}

/**
 * Écran de victoire acquitté (phase 5, A78, A82) — « Continuer : mode infini ».
 * Les crédits sont désormais vus (`stats.creditsSeen`). Au niveau 10 (le
 * Tricorne vient de tomber) : même effet qu'`acknowledgeInvasionWon` (niveau
 * 11, jour 1, monde du prochain combat et boss tirés). Victoire **différée** d'une partie déjà en
 * mode infini (niveau ≥ 11) : aucun niveau gagné, le monde manquant est tiré
 * et la partie reprend en Accalmie.
 *
 * v0.46.0 (A94-2) : *Artisan des dés*, premier nœud de l'Abîme, est **offert**
 * à l'entrée en mode infini (posé à 1 s'il vaut 0) ; `Game.acknowledgeVictory`
 * émet l'achat (`grantArtisan`).
 */
export function acknowledgeVictory(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase !== 'victory') return;
  s.stats.creditsSeen = true;
  grantArtisanLevel(s.nodes);
  if (s.level === ZONES.length) {
    acknowledgeInvasionWon(ctx);
    return;
  }
  ensureWorld(ctx);
  s.enemy = null;
  ctx.resetFlight();
  ctx.setPhase('accalmie');
}

/**
 * Défaite acquittée — **COMMENTS v0.4.1 §1.3** (+ A5) : une nouvelle run
 * démarre **immédiatement**, en Accalmie, niveau 1 jour 1. Le prestige, qui
 * s'intercalait ici jusqu'à la v0.4.x, n'existe plus.
 *
 * v0.51.0 (COMMENTS v0.50.0 §1.1, A101) : une partie qui a **débloqué le mode
 * infini** (`stats.creditsSeen`) repart au niveau **11**, jour 1, monde et
 * boss du niveau tirés comme après une Invasion gagnée ; les compteurs des
 * *Corrupted GachaDice* (`corruptedPurchases`) repartent de zéro, donc leurs
 * prix aussi (§1.2).
 *
 * Mur payant (A112) : `gateReached` repart à `false`.
 *
 * Perdus : l'or, les dés Maîtres équipés **et** ceux de l'inventaire.
 * Conservés : l'arbre de talents (donc le Gobelet, qui en dérive) et les
 * statistiques de méta-progression.
 *
 * *Conservation* (`arc_keep`) : le dé Maître de l'**emplacement 1** survit, avec
 * ses affixes et ses sorts. *Conservation II* (`arc_keep2`) et *Conservation
 * III* (`arc_keep3`) font de même pour les emplacements **2** et **3**
 * (COMMENTS v0.6.0 §1.1 / §D.18), *Conservation IV* et *V* (`arc_keep4`,
 * `arc_keep5`, v0.26.0) pour les emplacements **4** et **5** ; les emplacements
 * 6 et 7 (*Hexadice*, *Heptadice*) ne sont jamais conservés. Si l'emplacement 1 est vide (ou sans le nœud), le joueur reçoit un
 * « Dé de l'aïeul » neuf — d4 Commun, un sort commun tiré au hasard.
 */
export function acknowledgeRunLost(ctx: PhaseCtx): void {
  const s = ctx.state;
  if (s.phase !== 'runLost') return;

  /** Emplacements conservés : 0 par *Conservation*, 1 par II … 4 par V. */
  const keepNodes = ['arc_keep', 'arc_keep2', 'arc_keep3', 'arc_keep4', 'arc_keep5'];
  const kept: (MasterDie | null)[] = keepNodes.map((id, slot) => {
    if (nodeLevel(s.nodes, id) < 1) return null;
    const dieId = s.equipped[slot];
    return dieId ? (s.inventory.find((d) => d.id === dieId) ?? null) : null;
  });

  // A82 : défaite → niveau 1, en campagne ; A101 : niveau 11 une fois le mode
  // infini débloqué (crédits vus).
  s.level = s.stats.creditsSeen ? ENDLESS_FROM : 1;
  s.day = 1;
  s.wave = waveOf(s.level, 1);
  clearWorld(s);
  ensureWorld(ctx); // monde et boss du niveau 11 ; sans effet en campagne
  s.gold = 0;
  s.pendingGold = 0;
  s.runGold = 0;
  // A101 : les prix des Corrupted GachaDice repartent de leur base.
  s.corruptedPurchases = {};
  s.stats.runs = Math.max(s.stats.runs, 1);
  s.inventory = kept.filter((d): d is MasterDie => d !== null);
  s.equipped = new Array(Math.max(1, ctx.stats.slots)).fill(null);
  kept.forEach((die, slot) => {
    if (die && slot < s.equipped.length) s.equipped[slot] = die.id;
  });
  if (!kept[0]) {
    const starter = createStarterDie(ctx.rng);
    s.inventory.unshift(starter);
    s.equipped[0] = starter.id;
  }
  s.invasionReady = false;
  s.enemy = null;
  resetRunCounters(s);
  // A112 : la défaite repart au niveau 1 — rien n'est plus dû ; après achat,
  // le boss du niveau 2 se rebat comme pour tout joueur.
  setGateReached(ctx, false);
  ctx.resetFlight();
  ctx.setPhase('accalmie');
  ctx.emit('gold', { gold: s.gold });
}

/**
 * « Aller au devant » — spec §11.1.4 : déclenche l'Invasion en avance depuis
 * l'Accalmie. Les jours restants ne sont pas joués (ils sont simplement
 * sautés) ; l'Invasion se déroule exactement comme au jour 5.
 */
export function startInvasionEarly(ctx: PhaseCtx): boolean {
  const s = ctx.state;
  if (s.phase !== 'accalmie' || s.invasionReady) return false;
  startInvasion(ctx);
  return true;
}
